"use client";

import { useSearchParams } from "next/navigation";
import React, { createContext, useMemo, useState } from "react";
import type { Nullable } from "../../@types/generic";
import { useSetupBridge } from "../../bridge/hooks";
import type { InitialConfig, UserLocation } from "../../bridge/types";
import { debug } from "../../helpers/debug";

type WebviewContextValue = {
    webview: Nullable<string>;
    initialConfig: Nullable<InitialConfig>;
    userLocation: UserLocation | undefined;
};

export const WebviewContext =
    createContext<Nullable<WebviewContextValue>>(null);

type WebviewProviderProps = {
    children: React.ReactNode;
};

export const WebviewProvider: React.FC<WebviewProviderProps> = ({
    children,
}) => {
    const searchParams = useSearchParams();
    const [webview] = useState(() => searchParams.get("webview"));
    const [initialConfig, setInitialConfig] =
        useState<Nullable<InitialConfig>>(null);
    const [userLocation, setUserLocation] = useState<
        UserLocation | undefined
    >();

    useSetupBridge(webview, initialConfig, setInitialConfig, setUserLocation);

    const value = useMemo(() => {
        const nextValue = {
            webview,
            initialConfig,
            userLocation,
        };

        // eslint-disable-next-line testing-library/no-debugging-utils
        debug("[webview-context]", nextValue);

        return nextValue;
    }, [webview, initialConfig, userLocation]);

    return <WebviewContext value={value}>{children}</WebviewContext>;
};
